import type { PackageResponse } from '../../types/packages';
import { formatAed } from '../offers/offerReviewModel';
import { pick, PREVIEW_TEXT, PreviewLocale } from './packagePreviewText';

export interface PackagePreviewDetailProps {
  pkg: PackageResponse;
  locale: PreviewLocale;
}

/** The package page under the tile: description, included services (fixed choices; open choices as required rows), totals. */
export default function PackagePreviewDetail(props: PackagePreviewDetailProps) {
  const { pkg, locale } = props;
  const t = PREVIEW_TEXT[locale];
  const fixed = pkg.pricingMode === 'FIXED';
  const description = pick(locale, pkg.descriptionEn, pkg.descriptionAr);

  return (
    <div className="package-phone__detail">
      {description && <p className="package-phone__description">{description}</p>}
      <div className="package-phone__heading">{t.included}</div>
      <ul className="offer-phone__items">
        {pkg.items.map((item) => (
          <li key={item.offeringId} className="offer-phone__item package-phone__item">
            <div className="package-phone__item-main">
              <span className="offer-phone__item-name">{pick(locale, item.name, item.nameAr)}</span>
              {item.fixedChoices.length > 0 && (
                <span className="package-phone__choices">
                  {item.fixedChoices.map((c) => pick(locale, c.label, c.labelAr)).join(' · ')}
                </span>
              )}
              {!fixed && item.openVariations.map((v) => (
                <span key={v.variationId} className="package-phone__open" data-testid="package-open-choice">
                  {t.choose} {pick(locale, v.name, v.nameAr)} <span className="package-phone__required">{t.required}</span>
                </span>
              ))}
            </div>
            <span className="offer-phone__item-price" dir="ltr">
              {fixed ? <s>{formatAed(item.listPrice)}</s> : <>{t.from} {formatAed(item.listPrice)}</>}
            </span>
          </li>
        ))}
      </ul>
      <div className="package-phone__totals">
        {fixed ? (
          <>
            <div><span>{t.packagePrice}</span> <strong dir="ltr">{formatAed(pkg.packagePrice)}</strong></div>
            {pkg.savingAmount != null && (
              <div className="package-phone__saving">{t.youSave} <span dir="ltr">{formatAed(pkg.savingAmount)}</span></div>
            )}
          </>
        ) : (
          <div><span>{t.from}</span> <strong dir="ltr">{formatAed(pkg.fromPrice)}</strong> <span dir="ltr">(-{pkg.discountPercent}%)</span></div>
        )}
        {pkg.durationMinutes ? <div className="package-phone__duration">{t.minutes(pkg.durationMinutes)}</div> : null}
      </div>
      <p className="package-phone__promo">{t.promo}</p>
    </div>
  );
}
